/*
================================================================================
  FILE HEADER: SettingsPanel.js
  Description: React component for SettingsPanel. Controls for game options, reset, toggles, and logging.

  COPILOT FILE RULES (DO NOT DELETE THE HEADER OR FILE RULES FROM THE FILE)
  - This header and rules block must always be the very first content in the file.
  - Do not place any code, imports, or comments before this block.
  - If updating, always replace the entire header and rules block, never append or duplicate.
  - The format and placement must match the project standard exactly.
  - See Tile.js for the canonical example.
================================================================================
*/

// === Section: SettingsPanel Component ===
import React, { useState, useEffect, useRef } from 'react';
import pcModule from './SettingsPanel.pc.module.css';
import mobileModule from './SettingsPanel.mobile.module.css';
import useSelectedCssModule from '../hooks/useSelectedCssModule';
import { logDomEvent, logFunctionCall, isLoggerPortEnabled } from '../utils/spaLogger';
import { playNoteSequence } from '../utils/soundUtils';

// === Section: Logger Controls Defaults ===
const PORTS = [
  'Functions',
  'App',
  'Board',
  'Square',
  'Tile',
  'TileLayer',  
  'GameControls',
  'StartupDialog',
  'SettingsPanel',
  'VictoryDialog',
  'MoveHistoryDialog'
];

const DEFAULT_LOGGER_STATE = {
  spaLogger: false,
  consoleLogger: false,
  ports: PORTS.reduce((acc, port) => { acc[port] = true; return acc; }, {}),
  loggerHostOverride: false,
  loggerHost: 'localhost',
};

/**
 * SettingsPanel - Controls for game options, reset, toggles, and logging.
 * Props:
 *   - settings: current settings object
 *   - handleSettingsChange: function to update settings
 *   - resetGame: function to reset the game
 *   - setShowSettingsPanel: function to show/hide this panel
 */
function SettingsPanel({
  settings,
  handleSettingsChange,
  resetGame,
  setShowSettingsPanel,
  soundOn,
  setSoundOn,
  volume,
  setVolume,
  anchorRight,
  setSolveMovesActive,
  solveMovesActive,
  loggerTogglesLoaded
}) {
  const panelRef = useRef(null);
  const styles = useSelectedCssModule(pcModule, mobileModule);

  // === Section: Logger Controls State ===
  // Load logger state from localStorage if available
  const [loggerState, setLoggerState] = useState(() => {
    try {
      const saved = localStorage.getItem('spaLoggerState');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Defensive: ensure all ports and toggles are present
        const ports = { ...DEFAULT_LOGGER_STATE.ports, ...(parsed.ports || {}) };
        return { ...DEFAULT_LOGGER_STATE, ...parsed, ports };
      }
    } catch (e) { /* ignore */ }
    return { ...DEFAULT_LOGGER_STATE };
  });

  // Persist logger state to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem('spaLoggerState', JSON.stringify(loggerState));
    } catch (e) { /* ignore */ }
  }, [loggerState]);
  // Expose logger state globally for domLogger.js
  useEffect(() => {
    window.__SPA_LOGGER_STATE__ = loggerState;
  }, [loggerState]);

  // Log when settings panel is opened (once per mount)
  useEffect(() => {
    if (!loggerTogglesLoaded) return;
    if (panelRef.current) {
      const domHtml = panelRef.current.outerHTML;
      logDomEvent(
        'SettingsPanel',
        'settingsPanelOpen',
        panelRef.current.className || panelRef.current.tagName,
        domHtml,
        panelRef.current
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggerTogglesLoaded]);

  // Log when settings panel is closed
  const handleClosePanel = () => {
    if (!loggerTogglesLoaded) {
      setShowSettingsPanel(false);
      return;
    }
    if (panelRef.current) {
      const domHtml = panelRef.current.outerHTML;
      logDomEvent(
        'SettingsPanel',
        'settingsPanelClose',
        panelRef.current.className || panelRef.current.tagName,
        domHtml,
        panelRef.current
      );
    }
    setShowSettingsPanel(false);
  };
  // === Section: New Game Confirmation Dialog State ===
  const [showNewGameConfirm, setShowNewGameConfirm] = useState(false);

  // Helper: Check if a game is in progress (moveHistory or elapsed time > 0)
  const isGameInProgress = () => {
    return (settings.moveHistory && settings.moveHistory.length > 0) || (settings.elapsed && settings.elapsed > 0);
  };

  // Handler: New Game button click
  const handleNewGameClick = () => {
    if (isGameInProgress()) {
      setShowNewGameConfirm(true);
    } else {
      resetGame();
      setShowSettingsPanel(false);
    }
  };

  // Logger: Toggle controls
  const handleLoggedToggle = (key) => {
    if (!loggerTogglesLoaded) {
      handleSettingsChange({ [key]: !settings[key] });
      return;
    }
    if (panelRef.current) {
      logDomEvent(
        'SettingsPanel',
        'settingsToggle',
        panelRef.current.className || panelRef.current.tagName,
        panelRef.current.outerHTML,
        panelRef.current
      );
    }
    handleSettingsChange({ [key]: !settings[key] });
  };

  // Logger: Sound toggle
  const handleLoggedSoundToggle = () => {
    if (!loggerTogglesLoaded) {
      setSoundOn(!soundOn);
      return;
    }
    if (panelRef.current && isLoggerPortEnabled('SettingsPanel')) {
      logDomEvent(
        'SettingsPanel',
        'settingsToggle',
        panelRef.current.className || panelRef.current.tagName,
        panelRef.current.outerHTML,
        panelRef.current
      );
    }
    setSoundOn(!soundOn);
  };

  // Logger: Volume change
  const handleLoggedVolumeChange = (value) => {
    if (!loggerTogglesLoaded) {
      setVolume(value);
      return;
    }
    if (panelRef.current && isLoggerPortEnabled('SettingsPanel')) {
      logDomEvent(
        'SettingsPanel',
        'settingsVolume',
        panelRef.current.className || panelRef.current.tagName,
        panelRef.current.outerHTML,
        panelRef.current
      );
    }
    setVolume(value);
  };

  // Handler: Confirm New Game
  const handleConfirmNewGame = () => {
    setShowNewGameConfirm(false);
    resetGame();
    setShowSettingsPanel(false);
  };

  // Handler: Cancel New Game
  const handleCancelNewGame = () => {
    setShowNewGameConfirm(false);
  };

  // === Section: Sound String Tester State ===
  const [soundString, setSoundString] = useState('');
  const [playError, setPlayError] = useState('');

  if (!settings || !loggerTogglesLoaded) return null;

  // === Section: Solve Moves Toggle State ===
  // Only enabled after Shuffle Tiles (isShuffled prop required)
  // Highlight uses shimmerBorder (shimmerGreen) like other moveable tiles
  // When off, highlight is removed
  // This toggle is grouped with other toggles, above Sound String Tester

  // === Section: Handlers ===
  const handleToggle = (key) => {
    handleSettingsChange({ [key]: !settings[key] });
  };

  const handlePlaySoundString = () => {
    setPlayError('');
    logFunctionCall('SettingsPanel', 'play', { soundString, volume });
    try {
      playNoteSequence(soundString, volume);
    } catch (e) {
      setPlayError('Invalid sound string');
    }
  };

  return (
    <div ref={panelRef} className={anchorRight ? styles.settingsPanelAnchored : styles.settingsPanelOverlay}>
      <div className={styles.settingsPanel}>
        <div className={styles.settingsHeader}>
          <h3>Settings</h3>
          <button className={styles.closeBtn} onClick={handleClosePanel}>&times;</button>
        </div>
        <div className={styles.settingsOptions}>
          {/* === UPPER MENU: 2x2 GRID WITH VOLUME === */}
          <div className={styles.settingsControl + ' ' + styles.settingsControlStandard}>
            <div className={styles.settingsControlGrid}>
              <label className={styles.settingsControlLabel}>
                <input
                  type="checkbox"
                  checked={!!settings.showMoveHints}
                  onChange={() => {
                    if (solveMovesActive) {
                      // Block enabling Move Hints if Solve Moves is active
                      if (soundOn) playNoteSequence('blocked', volume);
                      return;
                    }
                    handleLoggedToggle('showMoveHints');
                  }}
                />
                Move Hints
              </label>
              <label className={styles.settingsControlLabel}>
                <input
                  type="checkbox"
                  checked={!!settings.showMoveHistory}
                  onChange={() => handleLoggedToggle('showMoveHistory')}
                />
                Move History
              </label>
              <label className={styles.settingsControlLabel}>
                <input
                  type="checkbox"
                  checked={!!settings.showFileRank}
                  onChange={() => handleLoggedToggle('showFileRank')}
                />
                Files Ranks
              </label>
              {styles === mobileModule && <div />}
              <label className={styles.settingsControlLabel}>
                <input
                  type="checkbox"
                  checked={soundOn}
                  onChange={handleLoggedSoundToggle}
                />
                Sound
              </label>
              <div className={styles.settingsUpperVolume}>
                <span
                  className={styles.settingsVolumeLabel + ' ' + (soundOn ? styles.settingsVolumeLabelOn : styles.settingsVolumeLabelOff)}
                >
                  Volume
                </span>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={volume}
                  onChange={e => handleLoggedVolumeChange(Number(e.target.value))}
                  disabled={!soundOn}
                  className={styles.settingsVolumeRange + ' ' + (soundOn ? styles.settingsVolumeRangeOn : styles.settingsVolumeRangeOff)}
                />
                <span
                  className={styles.settingsVolumeValue + ' ' + (soundOn ? styles.settingsVolumeLabelOn : styles.settingsVolumeLabelOff)}
                >
                  {Math.round(volume * 100)}%
                </span>
              </div>
            </div>
          </div>

          {/* === LOWER LIST: Power Ups (was logger-controls) === */}
          <div className={styles.settingsControl + ' ' + styles.settingsControlPowerups}>
            <div className={styles.settingsControlTitle}>Power Ups</div>
            <div className={styles.settingsControlGrid}>
              <label className={styles.settingsControlLabel}>
                <input
                  type="checkbox"
                  checked={settings.chessMovesEnabled}
                  onChange={() => handleLoggedToggle('chessMovesEnabled')}
                />
                Chess Moves
              </label>
              <label className={styles.settingsControlLabel}>
                <input
                  type="checkbox"
                  checked={settings.lineOfSightRequired}
                  onChange={() => handleLoggedToggle('lineOfSightRequired')}
                />
                Line of Sight
              </label>
              <label className={styles.settingsControlLabel}>
                <input
                  type="checkbox"
                  checked={settings.showSolvedIndicator}
                  onChange={() => handleLoggedToggle('showSolvedIndicator')}
                />
                Show Solved
              </label>
              <label className={styles.settingsControlLabel}>
                <input
                  type="checkbox"
                  checked={settings.debugMode}
                  onChange={() => handleLoggedToggle('debugMode')}
                />
                Debug Mode
              </label>
              <label className={styles.settingsControlLabel}>
                <input
                  type="checkbox"
                  checked={settings.testMoveMode}
                  onChange={() => handleLoggedToggle('testMoveMode')}
                />
                Test Moves
              </label>
              <label className={styles.settingsControlLabel}>
                <input
                  type="checkbox"
                  checked={!!solveMovesActive}
                  onChange={e => {
                    if (settings.showMoveHints && e.target.checked) {
                      // Block enabling Solve Moves if Move Hints is active
                      if (soundOn) playNoteSequence('blocked', volume);
                      return;
                    }
                    if (panelRef.current && isLoggerPortEnabled('SettingsPanel')) {
                      logDomEvent(
                        'SettingsPanel',
                        'settingsToggle',
                        panelRef.current.className || panelRef.current.tagName,
                        panelRef.current.outerHTML,
                        panelRef.current
                      );
                    }
                    setSolveMovesActive(e.target.checked);
                  }}
                />
                Solve Moves
              </label>
            </div>
          </div>

          {/* === LOGGER CONTROLS === */}
          {process.env.NODE_ENV !== 'production' && (
            <div className={styles.settingsControl + ' ' + styles.settingsControlLogger}>
              <div className={styles.settingsControlTitle}>Logging Controls</div>
              <div className={styles.settingsControlGrid}>
                <label className={styles.settingsControlLabel}>
                  <input
                    type="checkbox"
                    checked={loggerState.spaLogger}
                    onChange={() => setLoggerState(prev => ({ ...prev, spaLogger: !prev.spaLogger }))}
                  />
                  SPA Logger
                </label>
                <label className={styles.settingsControlLabel}>
                  <input
                    type="checkbox"
                    checked={loggerState.consoleLogger}
                    onChange={() => setLoggerState(prev => ({ ...prev, consoleLogger: !prev.consoleLogger }))}
                  />
                  Console Logger
                </label>
                {styles === pcModule && <div />}
                <label className={styles.settingsControlLabel}>
                  <input
                    type="checkbox"
                    checked={loggerState.loggerHostOverride}
                    onChange={() => setLoggerState(prev => ({ ...prev, loggerHostOverride: !prev.loggerHostOverride }))}
                  />
                  Override Host
                </label>
                <label className={styles.settingsControlLabel} style={{ marginLeft: 0, width: '100%' }}>
                  <input
                    type="text"
                    value={loggerState.loggerHost}
                    onChange={e => setLoggerState(prev => ({ ...prev, loggerHost: e.target.value }))}
                    placeholder="Logger Host (e.g. 192.168.1.14)"
                    style={{ width: '100%', height: '1.5em' }}
                    disabled={!loggerState.loggerHostOverride}
                  />
                </label>
              </div>
              <div className={styles.settingsControlPorts}>
                { /*
                 <label className={styles.settingsControlLabel}>
                  <input
                    type="checkbox"
                    checked={loggerState.functions}
                    onChange={() => setLoggerState(prev => ({ ...prev, functions: !prev.functions }))}
                  />
                  Functions
                </label>
                */ }

                {PORTS.map(port => (
                  <label key={port} className={styles.settingsControlPortLabel}>
                    <input
                      type="checkbox"
                      checked={loggerState.ports[port]}
                      onChange={() => setLoggerState(prev => ({ ...prev, ports: { ...prev.ports, [port]: !prev.ports[port] } }))}
                    />
                    {port}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* === Section: Sound String Tester (was soundStringTester) === */}
          <div className={styles.settingsControl + ' ' + styles.settingsControlSoundTester}>
            <div className={styles.soundStringTesterRow}>
              <span className={styles.soundStringTesterTitle}>Sound String Tester</span>
              <button
                className={styles.soundStringTesterPlay}
                onClick={e => handlePlaySoundString(e)}
              >
                Play
              </button>
              {playError && <span className={styles.soundStringTesterError}>{playError}</span>}
            </div>
            <textarea
              rows={2}
              value={soundString}
              className={styles.soundStringTesterTextarea}
              onFocus={e => {
                if (isLoggerPortEnabled('SettingsPanel')) {
                  logDomEvent(
                    'SettingsPanel',
                    'textareaFocus',
                    e.target.className || e.target.tagName,
                    e.target.outerHTML,
                    e.target
                  );
                }
              }}
              onChange={e => {
                setSoundString(e.target.value);
                if (isLoggerPortEnabled('SettingsPanel')) {
                  logDomEvent(
                    'SettingsPanel',
                    'textareaChange',
                    e.target.className || e.target.tagName,
                    e.target.outerHTML,
                    e.target
                  );
                }
              }}
              placeholder="Paste note string (e.g. 800:0.1;600:0.1;)"
            />
          </div>

          {/* === GAME CONTROLS === */}
          <div className={styles.settingsControl + ' ' + styles.settingsControlGame}>
            <div className={styles.settingsActionsCentered}>
              <button onClick={e => handleNewGameClick(e)} className={styles.resetBtn}>New Game</button>
              {showNewGameConfirm && (
                <div className={styles.newGameConfirmOverlay}>
                  <div className={styles.newGameConfirmDialog}>
                    <div className={styles.newGameConfirmMessage}>A game is in progress. Start a new game?</div>
                    <div className={styles.newGameConfirmActions}>
                      <button onClick={handleConfirmNewGame} className={styles.confirmBtn}>Yes</button>
                      <button onClick={handleCancelNewGame} className={styles.cancelBtn}>No</button>
                    </div>
                  </div>
                </div>
              )}
           </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPanel;
// === End Section: SettingsPanel Component ===